<template>
  <transition name="task-employee-upload-video-dialog" appear>
    <div
      v-if="dialog"
      class="task-employee-upload-video-dialog__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-employee-upload-video-title"
      @mousedown.self="onBack"
    >
      <div class="task-employee-upload-video-dialog__card">
        <div class="task-employee-upload-video-dialog__header">
          <div class="task-employee-upload-video-dialog__header-icon">
            <Icon name="add_video" />
          </div>
          <button
            type="button"
            class="task-employee-upload-video-dialog__close-btn"
            aria-label="Close"
            @click="onBack"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <div class="task-employee-upload-video-dialog__content">
          <div class="task-employee-upload-video-dialog__header-text">
            <span
              id="task-employee-upload-video-title"
              class="task-employee-upload-video-dialog__title"
            >
              {{ dialogTitle }}
            </span>
            <span class="task-employee-upload-video-dialog__description">
              <template v-if="isReassignMode">
                You are about to reassign the task to upload a microlearning video for<br />Checkpoint<br />{{
                  checkpointDescription || "—"
                }}
              </template>
              <template v-else>
                You are about to assign a task to an employee to upload a microlearning video for<br />Checkpoint<br />{{
                  checkpointDescription || "—"
                }}
              </template>
            </span>
          </div>
          <ExSelectPicker
            class="task-employee-upload-video-dialog__employee-picker"
            :class="{
              'task-employee-upload-video-dialog__employee-picker--selected': !!selectedEmployee,
            }"
            pickerLabel="Employee"
            label="Pick an Active Employee"
            searchLabel="Pick an Active Employee"
            placeholder="Pick an Active Employee"
            :items="pickerItems"
            :value="selectedEmployee"
            :isEmployeePicker="true"
            :showSelectedEmployeeCard="true"
            :useCode="true"
            :useDescription="true"
            :enablePagination="true"
            :hasMoreData="internalHasMore"
            :isLoadingMore="internalLoading"
            :isInitialLoading="internalLoading && internalEmployees.length === 0"
            :useBackendSearch="true"
            @item-selected="handleItemSelected"
            @clear-selection="resetSelection"
            @load-more="loadMoreEmployees"
            @search="handleSearch"
            :titleContentMaxWidth="'480px'"
            selected-employee-card-text-max-width="470px"
            ref="selectPicker"
          />

          <div class="task-employee-upload-video-dialog__deadline-row">
            <div class="task-employee-upload-video-dialog__deadline-picker">
              <DatePicker
                :value="deadline"
                :modal="true"
                :changeBadgeIndicator="true"
                :customCalendarIcon="true"
                :dataPickerPlaceHolder="'Add a Deadline'"
                :show-clear-icon="true"
                :min-date="deadlineMinDate"
                date-format="dd-MMM-yyyy"
                @onApplyDate="onDeadlineApplied"
              />
            </div>
            <div class="task-employee-upload-video-dialog__notify-wrapper">
              <SendNotificationInDialog
                :can_send_email="canNotifyEmployee"
                :notify="notifyEmployee"
                :is_employee="true"
                userLabel="Employee"
                @update:notify="notifyEmployee = $event"
              />
            </div>
          </div>

          <div class="task-employee-upload-video-dialog__actions">
            <ExButton
              v-if="wizard"
              label="Back"
              size="normal"
              outline="outline-hover"
              @onClick="onBack"
            />
            <ExButton
              v-if="wizard"
              :label="primaryButtonLabel"
              :normal="canAssignTask ? 'normal-default' : 'normal-disable'"
              :loading="$loader.isAppLoading ? 'left' : null"
              @onClick="onAssignTask"
            />
            <ExButton
              v-else
              :label="primaryButtonLabel"
              size="normal"
              :fullwidth="canAssignTask ? 'hover' : 'disable'"
              :loading="$loader.isAppLoading ? 'left' : null"
              @onClick="onAssignTask"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { getListofEmployeeV1 } from "@/api";
import "./taskEmployeeToUploadVideoDialog.css";
import ExButton from "@components/buttons/Button.vue";
import Icon from "@components/icons/Icon.vue";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import ExSelectPicker from "@components/SelectPicker/ExSelectPicker.vue";
import SendNotificationInDialog from "@components/SendNotificationInDialog/SendNotificationInDialog.vue";

const props = defineProps({
  dialog: {
    type: Boolean,
    default: false,
  },
  /** Checkpoint criteria or description to show in the dialog (e.g. "Can do this and that") */
  checkpointDescription: {
    type: String,
    default: "",
  },
  /**
   * Primary CTA — create flow keeps the default; inspector-only / propose-changes
   * (checkpoint already exists) should pass "Assign Task".
   * Ignored when mode is "reassign" (button reads "Reassign").
   */
  submitLabel: {
    type: String,
    default: "Add Checkpoint and Assign Task",
  },
  /**
   * "assign" (default) creates a new upload task; "reassign" updates the existing one.
   * Reassign mode: title/copy → "Reassign Task", pre-fills assignee + deadline, CTA "Reassign".
   */
  mode: {
    type: String,
    default: "assign",
    validator: (v) => ["assign", "reassign"].includes(v),
  },
  /** Pre-selected employee for reassign mode (picker item shape). */
  initialEmployee: {
    type: Object,
    default: null,
  },
  /** Pre-filled deadline for reassign mode (Date | ISO string | null). */
  initialDeadline: {
    type: [String, Date, Number],
    default: null,
  },
  /** Optional pre-loaded items; if not provided, employees are loaded via API (like PickEmployeeToIssueABadgeDialog) */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Visual/behavioural state of the dialog. When omitted, it is derived from the
   * current employee selection (Invalid / Valid with access / Valid without access).
   */
  state: {
    type: String,
    default: "",
    validator: (v) =>
      ["", "Invalid", "Valid", "Valid with access", "Valid without access"].includes(v),
  },
  /**
   * true : step 2 of the Create Checkpoint flow — paired "Back" + primary buttons.
   * false : standalone dialog — single full-width primary button.
   */
  wizard: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["back", "onAction"]);

const selectedEmployee = ref(null);
const deadline = ref(null);
const notifyEmployee = ref(true);
const selectPicker = ref(null);

const isReassignMode = computed(() => props.mode === "reassign");

const dialogTitle = computed(() =>
  isReassignMode.value ? "Reassign Task" : "Task employee to upload a video"
);

const canNotifyEmployee = computed(() => {
  if (props.state) {
    return props.state !== "Valid without access";
  }
  if (!selectedEmployee.value) return true;
  return !!selectedEmployee.value.access;
});

const primaryButtonLabel = computed(() => {
  if (isReassignMode.value) return "Reassign";
  return props.submitLabel;
});

// Internal employee list (loaded from API when dialog opens, same as PickEmployeeToIssueABadgeDialog)
const internalEmployees = ref([]);
const internalHasMore = ref(true);
const internalLoading = ref(false);
const internalSearchQuery = ref("");
const searchDebounceTimer = ref(null);
const employeePagination = ref({
  page: 1,
  size: 20,
  total: 0,
});

const deadlineMinDate = computed(() => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
});

const pickerItems = computed(() => {
  const source = internalEmployees.value.length > 0 ? internalEmployees.value : props.items || [];
  return source.map((item) => ({
    ...item,
    name: item.fullName ?? `${item.first_name || ""} ${item.last_name || ""}`.trim(),
    code: item.title ?? item.primary_positions?.[0]?.name ?? "",
  }));
});

function handleItemSelected(item) {
  selectedEmployee.value = item || null;
}

function resetSelection() {
  selectedEmployee.value = null;
  initializeInternalEmployees();
}

async function loadInternalEmployees() {
  if (internalLoading.value || !internalHasMore.value) return;
  try {
    internalLoading.value = true;
    const accountId = localStorage.getItem("currentAccountID");
    const offset = (employeePagination.value.page - 1) * employeePagination.value.size;
    const limit = employeePagination.value.size;

    const response = await getListofEmployeeV1(
      accountId,
      limit,
      offset,
      internalSearchQuery.value,
      "active",
      "first_name",
      false,
      undefined
    );

    if (response.data?.results) {
      const rawResults = response.data.results;
      // Silent filters only: keep operational, non-suspended employees regardless of
      // platform access (has_access is deliberately NOT filtered — see ER-11813).
      const formatted = rawResults
        .filter((el) => {
          const a = el.access;
          return a?.employee_status === "operational" && a?.is_suspended === false;
        })
        .map((el) => {
          const primaryPosition = el.primary_position ?? el.primary_positions?.[0];
          const titleFromCodeStr = primaryPosition?.code_str?.includes(" - ")
            ? primaryPosition.code_str.split(" - ").slice(1).join(" - ").trim()
            : "";
          const title =
            primaryPosition?.name ??
            titleFromCodeStr ??
            (el.primary_positions?.length > 0 ? el.primary_positions[0].name : "") ??
            "";
          return {
            id: el.id,
            access: !!el.access?.has_access,
            fullName: `${el.first_name || ""} ${el.last_name || ""}`.trim(),
            firstName: el.first_name,
            lastName: el.last_name,
            title,
            avatar: el.avatar,
            user: el.user,
            current_status: el.status,
            currentBadge: el.badges?.length > 0 ? el.badges.find((b) => b.is_current) : null,
            badgeList: el.badges,
          };
        });

      internalEmployees.value = [...internalEmployees.value, ...formatted];
      const total = response.data.count || 0;
      const consumed = offset + rawResults.length;
      employeePagination.value.total = total;
      employeePagination.value.page += 1;
      // Base hasMore on API pagination, not filtered list length (avoids endless load-more).
      internalHasMore.value = consumed < total && rawResults.length > 0;
    } else {
      internalHasMore.value = false;
    }
  } catch (error) {
    console.error("Error loading employees:", error);
    internalHasMore.value = false;
  } finally {
    internalLoading.value = false;
  }
}

async function initializeInternalEmployees() {
  internalEmployees.value = [];
  employeePagination.value = { ...employeePagination.value, page: 1 };
  internalHasMore.value = true;
  internalSearchQuery.value = "";
  await loadInternalEmployees();
}

function handleSearch(searchQuery) {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
  searchDebounceTimer.value = setTimeout(async () => {
    internalSearchQuery.value = searchQuery || "";
    employeePagination.value.page = 1;
    internalEmployees.value = [];
    internalHasMore.value = true;
    await loadInternalEmployees();
    searchDebounceTimer.value = null;
  }, 500);
}

async function loadMoreEmployees() {
  await loadInternalEmployees();
}

function onBack() {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
    searchDebounceTimer.value = null;
  }
  if (selectPicker.value?.backResetSelectedItem) {
    selectPicker.value.backResetSelectedItem();
  }
  selectedEmployee.value = null;
  deadline.value = null;
  notifyEmployee.value = true;
  emit("back");
}

function onDeadlineApplied(date) {
  deadline.value = date;
}

const canAssignTask = computed(() => {
  if (!selectedEmployee.value) return false;
  // Create/assign requires a deadline; reassign may preserve an existing deadline (optional clear).
  if (isReassignMode.value) return true;
  return Boolean(deadline.value);
});

function onAssignTask() {
  if (!canAssignTask.value) return;
  emit("onAction", {
    employee: selectedEmployee.value,
    deadline: deadline.value || null,
    notifyEmployee: notifyEmployee.value,
    mode: props.mode,
  });
}

function applyInitialReassignState() {
  if (!isReassignMode.value) return;
  if (props.initialEmployee) {
    selectedEmployee.value = {
      ...props.initialEmployee,
      name:
        props.initialEmployee.name ||
        props.initialEmployee.fullName ||
        `${props.initialEmployee.firstName || props.initialEmployee.first_name || ""} ${
          props.initialEmployee.lastName || props.initialEmployee.last_name || ""
        }`.trim(),
    };
  }
  if (props.initialDeadline != null && props.initialDeadline !== "") {
    const d = new Date(props.initialDeadline);
    deadline.value = Number.isNaN(d.getTime()) ? props.initialDeadline : d;
  }
}

watch(
  () => props.dialog,
  (val) => {
    if (val) {
      selectedEmployee.value = null;
      deadline.value = null;
      notifyEmployee.value = true;
      applyInitialReassignState();
      initializeInternalEmployees();
    } else {
      if (searchDebounceTimer.value) {
        clearTimeout(searchDebounceTimer.value);
        searchDebounceTimer.value = null;
      }
      selectedEmployee.value = null;
      deadline.value = null;
      notifyEmployee.value = true;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
});
</script>
